import { MdSend, MdExpandMore, MdSearch } from 'react-icons/md';
import type { SATheme } from '../../types/stylingAssistant.types';

interface SASearchInputProps {
  theme: SATheme;
  inputRef: React.RefObject<HTMLInputElement | null>;
  inputValue: string;
  isLoading: boolean;
  showSuggestionsDropdown: boolean;
  searchItems: string[];
  /** Direction the dropdown opens: "down" (default) or "up" (for bottom input) */
  dropdownDirection?: 'down' | 'up';
  onInputChange: (value: string) => void;
  onKeyPress: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onSend: () => void;
  onToggleSuggestions: () => void;
  onSuggestionClick: (suggestion: string) => void;
}

export default function SASearchInput({
  theme,
  inputRef,
  inputValue,
  isLoading,
  showSuggestionsDropdown,
  searchItems,
  dropdownDirection = 'down',
  onInputChange,
  onKeyPress,
  onSend,
  onToggleSuggestions,
  onSuggestionClick,
}: SASearchInputProps) {
  return (
    <div className="relative">
      <div
        className="flex items-center gap-3 px-4 py-2.5 rounded-2xl transition-all duration-200"
        style={{
          backgroundColor: 'rgba(255, 255, 255, 0.04)',
          border: `1.5px solid ${theme.border}`,
        }}
      >
        <MdSearch size={20} style={{ color: theme.textMuted }} className="flex-shrink-0" />
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={(e) => onInputChange(e.target.value)}
          onKeyDown={onKeyPress}
          placeholder="Ask me anything about style..."
          className="flex-1 bg-transparent outline-none text-sm placeholder:text-gray-500"
          style={{ color: theme.text }}
          disabled={isLoading}
        />
        <button
          onClick={onToggleSuggestions}
          className="p-1.5 rounded-lg hover:bg-white/5 transition-all flex-shrink-0 cursor-pointer"
          style={{ color: theme.textSecondary }}
          aria-label="Search suggestions"
        >
          <MdExpandMore
            size={20}
            className="transition-transform duration-200"
            style={{
              transform: showSuggestionsDropdown ? 'rotate(180deg)' : 'rotate(0deg)',
            }}
          />
        </button>
        <button
          onClick={onSend}
          disabled={!inputValue.trim() || isLoading}
          className="w-9 h-9 rounded-xl flex items-center justify-center transition-all hover:scale-105 disabled:opacity-30 disabled:cursor-not-allowed flex-shrink-0 cursor-pointer"
          style={{
            background: inputValue.trim()
              ? 'linear-gradient(135deg, #EE4423 0%, #ff6b35 100%)'
              : 'rgba(255, 255, 255, 0.08)',
          }}
          aria-label="Send"
        >
          <MdSend size={18} className="text-white" />
        </button>
      </div>

      {/* Search Suggestions Dropdown */}
      {showSuggestionsDropdown && searchItems.length > 0 && (
        <div
          className={`absolute left-0 right-0 rounded-2xl shadow-2xl z-50 max-h-72 overflow-y-auto ${
            dropdownDirection === 'up' ? 'bottom-full mb-2' : 'top-full mt-2'
          }`}
          style={{
            backgroundColor: '#1a1a1a',
            border: `1px solid rgba(238, 68, 35, 0.3)`,
          }}
        >
          <div className="p-3">
            <p className="text-[10px] font-semibold uppercase tracking-widest text-gray-500 px-3 mb-2">
              Suggestions
            </p>
            <div className="space-y-1">
              {searchItems.map((suggestion, idx) => (
                <button
                  key={idx}
                  onClick={() => onSuggestionClick(suggestion)}
                  className="w-full text-left px-3 py-3 rounded-xl transition-all hover:bg-white/5 flex items-center gap-3 cursor-pointer group"
                >
                  <MdSearch
                    size={16}
                    className="flex-shrink-0 text-gray-500 group-hover:text-[#EE4423] transition-colors"
                  />
                  <span className="text-sm" style={{ color: theme.text }}>
                    {suggestion}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
